/**
 * User Information Collector
 * Collects browser, device, and session information for contact forms
 */

/**
 * Parse User-Agent to get OS and Browser info
 */
const parseUserAgent = () => {
  const ua = navigator.userAgent;
  
  // Detect OS
  let os = 'Unknown';
  if (ua.includes('Windows NT 10')) os = 'Windows 10/11';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('iPhone')) os = 'iOS (iPhone)';
  else if (ua.includes('iPad')) os = 'iOS (iPad)';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('Linux')) os = 'Linux';
  
  // Detect Browser
  let browser = 'Unknown';
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
  
  // Detect Device Type
  let device = 'Desktop';
  if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    device = /iPad|Tablet/i.test(ua) ? 'Tablet' : 'Mobile';
  }
  
  return { os, browser, device };
};

/**
 * Get UTM parameters from URL
 */
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_term: params.get('utm_term') || '',
    utm_content: params.get('utm_content') || '',
  };
};

/**
 * Get stored UTM params from session (in case user navigates away from landing page)
 */
const getStoredUTMParams = () => {
  try {
    const stored = sessionStorage.getItem('villa_utm_params');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

/**
 * Store UTM params in session storage
 */
export const storeUTMParams = () => {
  const utmParams = getUTMParams();
  const hasUTM = Object.values(utmParams).some(v => v);
  
  if (hasUTM) {
    try {
      sessionStorage.setItem('villa_utm_params', JSON.stringify(utmParams));
    } catch {
      // Session storage not available
    }
  }
};

/**
 * Collect all user information for contact form
 * @param {object} options - Additional options
 * @param {string} options.villaId - Villa ID if on villa page
 * @param {string} options.villaName - Villa name if on villa page
 * @returns {object} User information object
 */
export const collectUserInfo = (options = {}) => {
  const { os, browser, device } = parseUserAgent();
  const currentUTM = getUTMParams();
  const storedUTM = getStoredUTMParams();
  const utmParams = Object.values(currentUTM).some(v => v) ? currentUTM : (storedUTM || currentUTM);
  
  return {
    // Villa info
    villa_id: options.villaId || '',
    villa_name: options.villaName || '',
    
    // Page info
    source_page: window.location.pathname,
    source_url: window.location.href,
    referrer: document.referrer || '',
    
    // User device info
    user_language: navigator.language || navigator.userLanguage || '',
    user_os: os,
    user_browser: browser,
    user_device: device,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    
    // UTM parameters
    ...utmParams,
    
    // Timestamp
    client_timestamp: new Date().toISOString(),
  };
};

/**
 * Initialize UTM tracking on page load
 */
export const initUTMTracking = () => {
  storeUTMParams();
};
